import type { IntegrationsRow, SyncJobProgress, SyncProgressByLocation } from 'app/types/integrations'

const SYNC_JOB_INTEGRATION: Readonly<Record<string, IntegrationsRow[`integration`]>> = {
  'kolla-sync': `Kolla`,
  'sikka-sync': `Sikka`,
  'carestack-sync': `Carestack`,
  'ascend-sync': `Ascend`,
}

// A task that dies without reporting back leaves its job incomplete forever. The API treats a
// PMS sync that started more than 36 hours ago as abandoned, so the bar stops showing it too.
const ABANDONED_SYNC_MS = 36 * 60 * 60 * 1000

/**
 * Picks each location's newest run per PMS sync and keeps it only while it is still running.
 * Choosing among finished runs too is what retires a stalled run: once a newer run of the same
 * sync finishes, the stalled one no longer holds the row at a frozen percentage.
 */
export function groupRunningSyncJobs(jobs: SyncJobProgress[], now = Date.now()): SyncProgressByLocation {
  const newest: SyncProgressByLocation = {}
  for (const job of jobs) {
    const integration = SYNC_JOB_INTEGRATION[job.type]
    if (!integration || !job.locationId) continue
    const forLocation = (newest[job.locationId] ??= {})
    const current = forLocation[integration]
    if (!current || new Date(current.createdAt) < new Date(job.createdAt)) forLocation[integration] = job
  }

  const running: SyncProgressByLocation = {}
  for (const [locationId, byIntegration] of Object.entries(newest)) {
    for (const [integration, job] of Object.entries(byIntegration)) {
      if (job.complete || now - new Date(job.createdAt).getTime() > ABANDONED_SYNC_MS) continue
      ;(running[locationId] ??= {})[integration as IntegrationsRow[`integration`]] = job
    }
  }
  return running
}
